import { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { Container } from "../Users/styles";
import { Button, Input, Select, TableColumnsType, Tag, Tooltip, Modal, Form, Space, Popconfirm, message, Card, Row, Col, DatePicker } from "antd";
import { SearchOutlined, CalendarOutlined, CloseCircleOutlined, CheckCircleFilled, LinkOutlined, SettingOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ArrowDownOutlined, ExperimentOutlined } from "@ant-design/icons";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend } from "recharts";
import { convertUtcToLocal } from "../../utils/helpers/convertUtcToLocal";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { selectConversionTracking } from "../../redux/features/conversionTracking/conversionTrackingSlice";
import { getConversionTracking, getConversionTrackingStats } from "../../redux/features/conversionTracking/conversionTrackingAsyncThunk";
import { ConversionTrackingItem } from "../../types/ConversionTracking";
import TestClaimsModal from "./TestClaimsModal";
import { RequestResults } from "../../redux/common/RequestStatus";
import { ClaimButton, LogsTable } from "../EmailLogs/styles";
import SystemPayRoutes from "../../utils/constants/Routes";
import DatePickerModal from "../../components/DatePickerModal";
import {
  FilterTabWrapper,
  FilterButton,
  CountBadge,
  StatusFilters,
  FilterInputs,
  StyledSelect,
} from "../Customers/styles";
import { LostStatuses } from "../../types/LostStatuses";
import { selectCampaigns, getAllCampaigns, createCampaign, updateCampaign, deleteCampaign, Campaign } from "../../redux/features/campaign/campaignSlice";

type TopFilterKey = "all" | "not_paid" | "paid";

interface TopFilterOption {
  key: TopFilterKey;
  label: string;
  colors: { background: string; border: string; text: string };
}

const topFilterOptions: TopFilterOption[] = [
  { key: "all", label: "All", colors: { background: "#ffffff", border: "#d1d5db", text: "#1f2937" } },
  { key: "not_paid", label: "Not Paid", colors: { background: "#fef2f2", border: "#ef4444", text: "#dc2626" } },
  { key: "paid", label: "Paid", colors: { background: "#ecfdf5", border: "#10b981", text: "#059669" } },
];

// Sub-status options shown in the dropdown
const subStatusOptions = [
  { value: "all", label: "All Sub-Statuses" },
  { value: String(LostStatuses.DraftIncomplete), label: "Draft Incomplete" },
  { value: String(LostStatuses.DraftCompleted), label: "Draft Completed" },
  { value: String(LostStatuses.NotPaid), label: "Not Paid" },
  { value: String(LostStatuses.Open), label: "Open" },
  { value: String(LostStatuses.Pending), label: "Pending" },
  { value: String(LostStatuses.Review), label: "Review" },
  { value: String(LostStatuses.InProcess), label: "In Process" },
  { value: String(LostStatuses.LFNotified), label: "LF Notified" },
  { value: String(LostStatuses.Urgent), label: "Urgent" },
  { value: String(LostStatuses.Overdue), label: "Overdue" },
  { value: String(LostStatuses.Deleted), label: "Deleted" },
];

const getStatusTag = (status: number, isPaid: boolean): { label: string; color: string; bg: string } => {
  switch (status) {
    case LostStatuses.DraftIncomplete: return { label: "Draft Incomplete", color: "#b45309", bg: "#fef3c7" };
    case LostStatuses.DraftCompleted: return { label: "Draft Completed", color: "#1d4ed8", bg: "#dbeafe" };
    case LostStatuses.NotPaid: return { label: "Not Paid", color: "#dc2626", bg: "#fef2f2" };
    case LostStatuses.Open: return { label: "Open", color: "#059669", bg: "#ecfdf5" };
    case LostStatuses.Pending: return { label: "Pending", color: "#7c3aed", bg: "#f5f3ff" };
    case LostStatuses.Review: return { label: "Review", color: "#2563eb", bg: "#eff6ff" };
    case LostStatuses.InProcess: return { label: "In Process", color: "#0891b2", bg: "#ecfeff" };
    case LostStatuses.LFNotified: return { label: "LF Notified", color: "#4f46e5", bg: "#eef2ff" };
    case LostStatuses.Urgent: return { label: "Urgent", color: "#dc2626", bg: "#fef2f2" };
    case LostStatuses.Overdue: return { label: "Overdue", color: "#be123c", bg: "#fff1f2" };
    case LostStatuses.Deleted: return { label: "Deleted", color: "#6b7280", bg: "#f3f4f6" };
    default: return { label: isPaid ? "Paid" : "Unknown", color: "#6b7280", bg: "#f3f4f6" };
  }
};

const parseUtmParams = (url: string | undefined): { source?: string; medium?: string; campaign?: string } | null => {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const source = urlObj.searchParams.get("utm_source");
    const medium = urlObj.searchParams.get("utm_medium");
    const campaign = urlObj.searchParams.get("utm_campaign");
    if (source || medium || campaign) {
      return { ...(source && { source }), ...(medium && { medium }), ...(campaign && { campaign }) };
    }
    return null;
  } catch { return null; }
};

const getAdCampaignDisplay = (visitorInfo: ConversionTrackingItem["visitorInfo"], campaigns: Campaign[]): { label: string; matched: boolean } => {
  if (!visitorInfo?.entryUrl) return { label: "-", matched: false };
  const entryUrl = visitorInfo.entryUrl.toLowerCase();

  // Match against admin-defined campaigns first
  for (const campaign of campaigns) {
    if (entryUrl.includes(campaign.urlPattern.toLowerCase())) {
      return { label: campaign.label, matched: true };
    }
  }

  // Fallback to UTM params
  const utm = parseUtmParams(visitorInfo.entryUrl);
  if (utm) {
    const parts: string[] = [];
    if (utm.source) parts.push(utm.source);
    if (utm.medium) parts.push(utm.medium);
    if (utm.campaign) parts.push(utm.campaign);
    return { label: parts.join(" / "), matched: false };
  }
  if (visitorInfo.referrer) {
    try { return { label: `Referral: ${new URL(visitorInfo.referrer).hostname}`, matched: false }; }
    catch { return { label: "Referral", matched: false }; }
  }
  return { label: "Direct", matched: false };
};

const ConversionTracking = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(10);
  const [searchField, setSearchField] = useState<string | undefined>();
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [topFilter, setTopFilter] = useState<TopFilterKey>("all");
  const [subStatus, setSubStatus] = useState<string>("all");
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string | undefined>();
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [dateField, setDateField] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();

  const [trendMode, setTrendMode] = useState<"daily" | "weekly" | "monthly">("weekly");

  // Dashboard date range
  type DatePreset = "today" | "this_week" | "this_month" | "last_3m" | "last_6m" | "1_year" | "this_year" | "all_time" | "custom";

  // Range bounds are computed as UTC ISO timestamps from the admin's LOCAL day boundaries
  // (dayjs uses the browser TZ by default). Sending YYYY-MM-DD strings and letting the
  // backend run DATE() on a UTC timestamp gives off-by-one results for US/EU admins —
  // a "today" pick in PDT bled into the next UTC day. Sending precise UTC timestamps and
  // having the backend do a straight `createdAt >= :from AND createdAt <= :to` fixes that.
  const buildRange = (from: dayjs.Dayjs, to: dayjs.Dayjs) => ({
    from: from.startOf("day").toISOString(),
    to: to.endOf("day").toISOString(),
  });

  // Default to "today" so admins land on actionable data instead of all-time.
  const initialTodayRange = buildRange(dayjs(), dayjs());
  const [dashDatePreset, setDashDatePreset] = useState<DatePreset>("today");
  const [dashFromDate, setDashFromDate] = useState<string | undefined>(initialTodayRange.from);
  const [dashToDate, setDashToDate] = useState<string | undefined>(initialTodayRange.to);

  const getDateRange = (preset: DatePreset): { from?: string; to?: string } => {
    const today = dayjs();
    switch (preset) {
      case "today": return buildRange(today, today);
      case "this_week": return buildRange(today.startOf("week"), today);
      case "this_month": return buildRange(today.startOf("month"), today);
      case "last_3m": return buildRange(today.subtract(3, "month"), today);
      case "last_6m": return buildRange(today.subtract(6, "month"), today);
      case "1_year": return buildRange(today.subtract(1, "year"), today);
      case "this_year": return buildRange(today.startOf("year"), today);
      case "all_time": return {};
      case "custom": return { from: dashFromDate, to: dashToDate };
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    setDashDatePreset(preset);
    if (preset !== "custom") {
      const { from, to } = getDateRange(preset);
      setDashFromDate(from);
      setDashToDate(to);
    }
  };

  const {
    data, status: loadingStatus, counts, totalCount,
    draftIncompleteCount, draftCompletedCount, notPaidCount,
    paidCount, overdueCount, deletedCount,
    stats, statsStatus,
  } = useAppSelector(selectConversionTracking);
  const { data: campaigns } = useAppSelector(selectCampaigns);
  const dispatch = useAppDispatch();

  // Campaign management state
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [campaignForm] = Form.useForm();
  // Test-claim management modal
  const [isTestClaimsModalOpen, setIsTestClaimsModalOpen] = useState(false);

  // Effective date range that drives the WHOLE page (top tiles, charts, list).
  // Date pill / range picker takes precedence; the legacy table-only DateModal filter
  // is used only when no pill is active (dashDatePreset === "all_time" with no custom range).
  const effectiveDateRange = useMemo(() => {
    const { from, to } = getDateRange(dashDatePreset);
    if (from && to) return { fromDate: from, toDate: to, dateField: 'lost."createdAt"' };
    if (fromDate && toDate) return { fromDate, toDate, dateField: dateField || 'lost."createdAt"' };
    return { fromDate: undefined as string | undefined, toDate: undefined as string | undefined, dateField: '' };
  }, [dashDatePreset, dashFromDate, dashToDate, fromDate, toDate, dateField]);

  // After a test-flag is toggled, refresh both the table data and the stats panel
  // so counts and revenue tiles reflect the new exclusion immediately.
  const refreshTrackingData = useCallback(() => {
    dispatch(getConversionTrackingStats({ fromDate: effectiveDateRange.fromDate, toDate: effectiveDateRange.toDate, dateField: effectiveDateRange.fromDate && effectiveDateRange.toDate ? effectiveDateRange.dateField : undefined }));
    dispatch(getConversionTracking({
      offset: (currentPage - 1) * currentLimit,
      limit: currentLimit,
      searchField,
      searchValue: debouncedSearchValue?.trim(),
      fromDate: effectiveDateRange.fromDate,
      toDate: effectiveDateRange.toDate,
      dateField: effectiveDateRange.fromDate && effectiveDateRange.toDate ? effectiveDateRange.dateField : "",
      statusFilter: getBackendStatusFilter(),
    }));
  }, [dispatch, effectiveDateRange, currentPage, currentLimit, searchField, debouncedSearchValue, topFilter, subStatus]);

  useEffect(() => { dispatch(getAllCampaigns()); }, [dispatch]);

  // Fetch stats whenever the effective date range changes. Same source of truth as the list
  // query — pills take precedence over the legacy DateModal filter.
  useEffect(() => {
    dispatch(getConversionTrackingStats({
      fromDate: effectiveDateRange.fromDate,
      toDate: effectiveDateRange.toDate,
      dateField: effectiveDateRange.fromDate && effectiveDateRange.toDate ? effectiveDateRange.dateField : undefined,
    }));
  }, [dispatch, effectiveDateRange]);


  const handleCampaignSave = useCallback(async () => {
    try {
      const values = await campaignForm.validateFields();
      if (editingCampaign) {
        await dispatch(updateCampaign({ id: editingCampaign.id, ...values })).unwrap();
        message.success("Campaign updated");
      } else {
        await dispatch(createCampaign(values)).unwrap();
        message.success("Campaign created");
      }
      campaignForm.resetFields();
      setEditingCampaign(null);
    } catch { /* validation error */ }
  }, [dispatch, campaignForm, editingCampaign]);

  const handleCampaignDelete = useCallback(async (id: string) => {
    await dispatch(deleteCampaign(id)).unwrap();
    message.success("Campaign deleted");
  }, [dispatch]);

  const handleEditCampaign = useCallback((campaign: Campaign) => {
    setEditingCampaign(campaign);
    campaignForm.setFieldsValue({ label: campaign.label, urlPattern: campaign.urlPattern, currency: campaign.currency || 'USD' });
  }, [campaignForm]);

  const handleCancelEdit = useCallback(() => {
    setEditingCampaign(null);
    campaignForm.resetFields();
  }, [campaignForm]);

  // "Not Paid" group = Draft Incomplete + Draft Completed + Not Paid + Overdue + Deleted
  const notPaidGroupCount = draftIncompleteCount + draftCompletedCount + notPaidCount + overdueCount + deletedCount;

  const handleSearch = (value: string) => { setSearchValue(value); setCurrentPage(1); };
  const handleSearchFieldChange = (value: string | null) => { setSearchField(value || undefined); setCurrentPage(1); };
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleLimitChange = (limit: number) => { setCurrentLimit(limit); setCurrentPage(1); };

  const handleTopFilterChange = (key: TopFilterKey) => {
    setTopFilter(key);
    setSubStatus("all");
    setCurrentPage(1);
  };

  const handleSubStatusChange = (value: string) => {
    setSubStatus(value);
    setCurrentPage(1);
  };

  const handleDateModalOk = (params: { dateField?: string; fromDate?: string; toDate?: string }) => {
    setDateField(params.dateField); setFromDate(params.fromDate); setToDate(params.toDate);
    setIsDateModalOpen(false); setCurrentPage(1);
  };

  const handleClearDateFilter = () => { setDateField(undefined); setFromDate(undefined); setToDate(undefined); setCurrentPage(1); };

  const handleClickOnClaim = (record: ConversionTrackingItem) => {
    if (record?.trackingId) window.open(`${SystemPayRoutes.reportBase}/${record.trackingId}?withDeleted=true`, "_blank");
  };

  // Compute backend statusFilter value
  const getBackendStatusFilter = (): number | undefined | null => {
    // If sub-status is selected, it takes priority
    if (subStatus !== "all") return Number(subStatus);

    switch (topFilter) {
      case "all": return undefined; // no filter
      case "paid": return -1; // special: all paid statuses (Open, Pending, Review, InProcess, LFNotified, Urgent)
      case "not_paid": return -2; // special: all not-paid statuses
      default: return undefined;
    }
  };

  // Get sub-status options filtered by top tab
  const filteredSubStatusOptions = useMemo(() => {
    if (topFilter === "not_paid") {
      return [
        { value: "all", label: "All Sub-Statuses" },
        { value: String(LostStatuses.DraftIncomplete), label: "Draft Incomplete" },
        { value: String(LostStatuses.DraftCompleted), label: "Draft Completed" },
        { value: String(LostStatuses.NotPaid), label: "Not Paid" },
        { value: String(LostStatuses.Overdue), label: "Overdue" },
        { value: String(LostStatuses.Deleted), label: "Deleted" },
      ];
    }
    if (topFilter === "paid") {
      return [
        { value: "all", label: "All Sub-Statuses" },
        { value: String(LostStatuses.Open), label: "Open" },
        { value: String(LostStatuses.Pending), label: "Pending" },
        { value: String(LostStatuses.Review), label: "Review" },
        { value: String(LostStatuses.InProcess), label: "In Process" },
        { value: String(LostStatuses.LFNotified), label: "LF Notified" },
        { value: String(LostStatuses.Urgent), label: "Urgent" },
      ];
    }
    return subStatusOptions;
  }, [topFilter]);

  const columns: TableColumnsType<any> = useMemo(
    () => [
      { title: "#", key: "index", render: (_, __, index) => `#${index + 1 + (currentPage - 1) * currentLimit}`, width: 45 },
      { title: "Campaign", key: "adCampaign", width: 120, ellipsis: true, render: (_, record) => {
        const { label: display, matched } = getAdCampaignDisplay(record.visitorInfo, campaigns);
        const tipText = matched ? `${display} (matched campaign)` : display;
        return (
          <Tooltip title={tipText}>
            <div style={{ fontWeight: 500, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: matched ? "#2563eb" : undefined }}>{display}</div>
          </Tooltip>
        );
      } },
      { title: "Entry URL", key: "entryUrl", width: 180, ellipsis: true, render: (_, record) => {
        const entryUrl = record.visitorInfo?.entryUrl;
        if (!entryUrl) return <div style={{ color: "#9ca3af" }}>-</div>;
        return (
          <Tooltip title={entryUrl}>
            <div style={{ fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <LinkOutlined style={{ marginRight: 3 }} />
              {(() => { try { const u = new URL(entryUrl); return u.pathname + u.search; } catch { return entryUrl; } })()}
            </div>
          </Tooltip>
        );
      } },
      { title: "Claim #", dataIndex: "trackingId", key: "trackingId", render: (_, record) => (
        <ClaimButton type="link" onClick={() => handleClickOnClaim(record)}>{record.trackingId || "-"}</ClaimButton>
      ), width: 95 },
      { title: "Customer", key: "customer", width: 200, ellipsis: true, render: (_, record) => {
        const name = record.customerName?.trim() || "-";
        const email = record.customerEmail || "";
        return (
          <Tooltip title={<>{name}<br />{email}</>}>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontWeight: 500, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
              {email && <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</div>}
            </div>
          </Tooltip>
        );
      } },
      { title: "Status", dataIndex: "status", key: "status", width: 120, render: (val: number, record) => {
        const { label, color, bg } = getStatusTag(val, record.isPaid);
        return (
          <Tooltip title={label}>
            <Tag style={{ color, backgroundColor: bg, borderColor: color, fontWeight: 600, fontSize: 11, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle" }}>{label}</Tag>
          </Tooltip>
        );
      } },
      { title: "Plan / Amount", key: "plan", width: 140, ellipsis: true, render: (_, record) => {
        const planName = record.planName || "";
        const amount = record.amountPaid ? `$${Number(record.amountPaid).toFixed(2)}` : "";
        const tipText = [planName, amount].filter(Boolean).join(" - ") || "-";
        if (planName && amount) return (
          <Tooltip title={tipText}>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontWeight: 500, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{planName}</div>
              <div style={{ fontSize: 11, color: "#059669" }}>{amount}</div>
            </div>
          </Tooltip>
        );
        if (amount) return <div style={{ color: "#059669", fontSize: 12 }}>{amount}</div>;
        return <div style={{ color: "#9ca3af" }}>-</div>;
      } },
      { title: "Paid", dataIndex: "paidDate", key: "paidDate", width: 80, render: (date) => {
        const formatted = date ? convertUtcToLocal(date, "MM/DD/YY") : "-";
        const full = date ? convertUtcToLocal(date, "MM/DD/YYYY hh:mm A") : "-";
        return <Tooltip title={full}><div style={{ fontSize: 12, whiteSpace: "nowrap" }}>{formatted}</div></Tooltip>;
      } },
      { title: "Location", key: "location", width: 140, ellipsis: true, render: (_, record) => {
        const parts: string[] = [];
        if (record.establishmentLocationCode) parts.push(record.establishmentLocationCode);
        else if (record.establishmentLocationName) parts.push(record.establishmentLocationName);
        if (record.establishmentTypeName) parts.push(record.establishmentTypeName);
        const text = parts.length > 0 ? parts.join(" - ") : "-";
        return (
          <Tooltip title={text}>
            <div style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{text}</div>
          </Tooltip>
        );
      } },
      { title: "Category", key: "category", width: 110, ellipsis: true, render: (_, record) => {
        const cat = record.categoryNames || "-";
        const sub = record.subCategoryNames || "";
        return (
          <Tooltip title={<>{cat}{sub && <><br />{sub}</>}</>}>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cat}</div>
              {sub && <div style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
            </div>
          </Tooltip>
        );
      } },
      { title: "Filed", dataIndex: "dateFiled", key: "dateFiled", width: 80, render: (date) => {
        const formatted = date ? convertUtcToLocal(date, "MM/DD/YY") : "-";
        const full = date ? convertUtcToLocal(date, "MM/DD/YYYY hh:mm A") : "-";
        return <Tooltip title={full}><div style={{ fontSize: 12, whiteSpace: "nowrap" }}>{formatted}</div></Tooltip>;
      } },
    ],
    [currentPage, currentLimit, campaigns]
  );

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      getConversionTracking({
        offset: (currentPage - 1) * currentLimit,
        limit: currentLimit,
        searchField,
        searchValue: debouncedSearchValue?.trim(),
        fromDate: effectiveDateRange.fromDate,
        toDate: effectiveDateRange.toDate,
        dateField: effectiveDateRange.fromDate && effectiveDateRange.toDate ? effectiveDateRange.dateField : "",
        statusFilter: getBackendStatusFilter(),
      })
    );
  }, [dispatch, currentPage, currentLimit, searchField, debouncedSearchValue, effectiveDateRange, topFilter, subStatus]);

  const isDateRangeSelected = useMemo(() => !!(fromDate && toDate), [fromDate, toDate]);
  const formattedDateRange = useMemo(() => {
    return fromDate && toDate ? `${dayjs(fromDate).format("MM/DD/YYYY")} - ${dayjs(toDate).format("MM/DD/YYYY")}` : "Date Range";
  }, [fromDate, toDate]);

  const getTopCount = (key: TopFilterKey): number => {
    switch (key) {
      case "all": return totalCount;
      case "not_paid": return notPaidGroupCount;
      case "paid": return paidCount;
      default: return 0;
    }
  };

  // ── Dashboard computations from stats endpoint (full dataset) ──
  const PIE_COLORS = ["#f59e0b", "#3b82f6", "#ef4444", "#10b981", "#8b5cf6", "#0891b2", "#f43f5e", "#6b7280"];

  const kpis = useMemo(() => {
    const total = totalCount || 0;
    const paid = paidCount || 0;
    const conversionRate = total > 0 ? ((paid / total) * 100).toFixed(1) : "0.0";
    const totalRevenue = stats?.totalRevenue || 0;
    const avgRevenue = paid > 0 ? totalRevenue / paid : 0;
    return { total, paid, notPaid: notPaidGroupCount, conversionRate, totalRevenue, avgRevenue };
  }, [totalCount, paidCount, notPaidGroupCount, stats]);

  const funnelData = useMemo(() => [
    { name: "Draft Incomplete", value: draftIncompleteCount, color: "#f59e0b" },
    { name: "Draft Completed", value: draftCompletedCount, color: "#3b82f6" },
    { name: "Not Paid", value: notPaidCount, color: "#ef4444" },
    { name: "Paid", value: paidCount, color: "#10b981" },
  ], [draftIncompleteCount, draftCompletedCount, notPaidCount, paidCount]);

  // Trend data (toggle daily/weekly/monthly)
  const trendData = useMemo(() => {
    if (!stats) return [];
    const raw = trendMode === "daily" ? stats.dailyTrend : trendMode === "weekly" ? stats.weeklyTrend : stats.monthlyTrend;
    const dateKey = trendMode === "daily" ? "day" : trendMode === "weekly" ? "week" : "month";
    return raw.map((r: any) => ({
      label: dayjs(r[dateKey]).format(trendMode === "monthly" ? "MMM YY" : "MMM DD"),
      total: Number(r.total),
      paid: Number(r.paid),
      revenue: r.revenue ? Number(r.revenue) : 0,
    }));
  }, [stats, trendMode]);

  // Campaign performance (from allVisitorData in stats - full dataset)
  const campaignPerformance = useMemo(() => {
    if (!stats?.allVisitorData) return [];
    const paidStatuses = [LostStatuses.Open, LostStatuses.Pending, LostStatuses.Review, LostStatuses.InProcess, LostStatuses.LFNotified, LostStatuses.Urgent];
    const buckets: Record<string, { name: string; total: number; paid: number; notPaid: number; revenue: number; convRate: string }> = {};
    stats.allVisitorData.forEach((d) => {
      let visitorInfo = null;
      try { visitorInfo = d.visitorInfoText ? JSON.parse(d.visitorInfoText) : null; } catch { /* */ }
      const { label } = getAdCampaignDisplay(visitorInfo, campaigns);
      if (!buckets[label]) buckets[label] = { name: label, total: 0, paid: 0, notPaid: 0, revenue: 0, convRate: "0" };
      buckets[label].total++;
      if (paidStatuses.includes(d.status)) {
        buckets[label].paid++;
        buckets[label].revenue += d.amountPaid ? Number(d.amountPaid) : 0;
      } else {
        buckets[label].notPaid++;
      }
    });
    return Object.values(buckets)
      .map((b) => ({ ...b, convRate: b.total > 0 ? ((b.paid / b.total) * 100).toFixed(1) : "0" }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [stats, campaigns]);

  // Device & browser pie data
  const deviceData = useMemo(() => (stats?.deviceBreakdown || []).map((d) => ({ name: d.device, value: d.total })), [stats]);
  const browserData = useMemo(() => (stats?.browserBreakdown || []).map((d) => ({ name: d.browser, value: d.total })), [stats]);

  // Top locations
  const locationData = useMemo(() => (stats?.topLocations || []).map((l) => ({
    name: l.code || l.name, total: Number(l.total), paid: Number(l.paid), revenue: Number(l.revenue),
  })), [stats]);

  // Top categories
  const categoryData = useMemo(() => (stats?.topCategories || []).map((c) => ({
    name: c.name, total: Number(c.total), paid: Number(c.paid),
  })), [stats]);

  // Top plans
  const planData = useMemo(() => (stats?.topPlans || []).map((p) => ({
    name: p.name, total: Number(p.total), revenue: Number(p.revenue), price: p.planAmount,
  })), [stats]);

  const filteredData = useMemo(() => {
    if (campaignFilter === "all") return data;
    if (campaignFilter === "_direct") {
      return data.filter((item) => {
        const { matched } = getAdCampaignDisplay(item.visitorInfo, campaigns);
        const hasUtm = item.visitorInfo?.entryUrl && parseUtmParams(item.visitorInfo.entryUrl);
        const hasReferrer = item.visitorInfo?.referrer;
        return !matched && !hasUtm && !hasReferrer;
      });
    }
    if (campaignFilter === "_unmatched") {
      return data.filter((item) => {
        const { matched } = getAdCampaignDisplay(item.visitorInfo, campaigns);
        return !matched;
      });
    }
    // Filter by specific campaign urlPattern
    const campaign = campaigns.find((c) => c.id === campaignFilter);
    if (!campaign) return data;
    return data.filter((item) => {
      const entryUrl = item.visitorInfo?.entryUrl?.toLowerCase() || "";
      return entryUrl.includes(campaign.urlPattern.toLowerCase());
    });
  }, [data, campaignFilter, campaigns]);

  return (
    <Container>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Conversion Tracking</h2>
          <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 14 }}>
            Track leads, payment status, and ad campaign attribution for all reports
          </p>
        </div>
        <Space>
          <Button icon={<ExperimentOutlined />} onClick={() => setIsTestClaimsModalOpen(true)}>
            Manage Test Claims
          </Button>
          <Button icon={<SettingOutlined />} onClick={() => setIsCampaignModalOpen(true)}>
            Manage Campaigns
          </Button>
        </Space>
      </div>

      {/* ── Date Presets ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, padding: "8px 12px", background: "#fff", borderRadius: 6, boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
        {([
          { key: "today", label: "Today" },
          { key: "this_week", label: "This Week" },
          { key: "this_month", label: "This Month" },
          { key: "last_3m", label: "Last 3 Months" },
          { key: "last_6m", label: "Last 6 Months" },
          { key: "1_year", label: "1 Year" },
          { key: "this_year", label: "This Year" },
          { key: "all_time", label: "All Time" },
        ] as { key: DatePreset; label: string }[]).map((p) => (
          <Button
            key={p.key}
            size="small"
            type={dashDatePreset === p.key ? "primary" : "default"}
            onClick={() => handlePresetClick(p.key)}
            style={{ borderRadius: 14, fontSize: 12, padding: "0 12px", height: 30 }}
          >
            {p.label}
          </Button>
        ))}
        {dashDatePreset !== "all_time" && (
          <Button size="small" type="link" danger onClick={() => handlePresetClick("all_time")} style={{ fontSize: 12, height: 30 }}>
            Clear
          </Button>
        )}
        <div style={{ flex: 1 }} />
        <DatePicker.RangePicker
          size="small"
          style={{ width: 220, flexShrink: 0 }}
          format="MM/DD/YYYY"
          value={dashFromDate && dashToDate ? [dayjs(dashFromDate), dayjs(dashToDate)] as any : null}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setDashDatePreset("custom");
              // Same TZ-aware convention as the preset buttons: send UTC ISO timestamps
              // representing the admin's local-TZ day boundaries.
              const range = buildRange(dates[0], dates[1]);
              setDashFromDate(range.from);
              setDashToDate(range.to);
            }
          }}
        />
      </div>

      {/* ── KPI Cards ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { label: "Total Leads", value: kpis.total, color: "#3b82f6", sub: "" },
          { label: "Conversion Rate", value: `${kpis.conversionRate}%`, color: "#10b981", sub: `${kpis.paid} paid of ${kpis.total}` },
          { label: "Not Paid / Dropped", value: kpis.notPaid, color: "#ef4444", sub: `${kpis.total > 0 ? ((kpis.notPaid / kpis.total) * 100).toFixed(1) : 0}% drop-off` },
          { label: "Total Revenue", value: `$${kpis.totalRevenue.toFixed(2)}`, color: "#8b5cf6", sub: `Avg: $${kpis.avgRevenue.toFixed(2)} per paid` },
        ].map((k) => (
          <Col xs={12} sm={6} key={k.label}>
            <Card size="small" style={{ borderLeft: `3px solid ${k.color}` }}>
              <div style={{ color: "#6b7280", fontSize: 11, marginBottom: 2 }}>{k.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#1f2937" }}>{k.value}</div>
              {k.sub && <div style={{ fontSize: 11, color: "#6b7280" }}>{k.sub}</div>}
            </Card>
          </Col>
        ))}
      </Row>

      {/* ── Row 1: Funnel + Trend + Campaign Leads ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Conversion Funnel</span>} styles={{ body: { padding: "12px 16px" } }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {funnelData.map((step, i) => {
                const maxVal = Math.max(...funnelData.map((f) => f.value), 1);
                const pct = (step.value / maxVal) * 100;
                const dropOff = i > 0 && funnelData[i - 1].value > 0
                  ? (((funnelData[i - 1].value - step.value) / funnelData[i - 1].value) * 100).toFixed(0) : null;
                return (
                  <div key={step.name}>
                    {i > 0 && dropOff && (
                      <div style={{ textAlign: "center", fontSize: 9, color: "#ef4444", margin: "-1px 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                        <ArrowDownOutlined style={{ fontSize: 8 }} /> {dropOff}%
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 80, fontSize: 10, color: "#374151", flexShrink: 0, textAlign: "right" }}>{step.name}</div>
                      <div style={{ flex: 1, background: "#f3f4f6", borderRadius: 4, height: 22, position: "relative", overflow: "hidden" }}>
                        <div style={{ width: `${pct}%`, background: step.color, height: "100%", borderRadius: 4, transition: "width 0.5s ease", minWidth: step.value > 0 ? 18 : 0 }} />
                        <span style={{ position: "absolute", right: 4, top: 3, fontSize: 10, fontWeight: 600, color: pct > 50 ? "#fff" : "#374151" }}>{step.value}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Card size="small" title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Leads Over Time</span>
              <Select size="small" value={trendMode} onChange={setTrendMode} style={{ width: 100 }}
                options={[{ value: "daily", label: "Daily" }, { value: "weekly", label: "Weekly" }, { value: "monthly", label: "Monthly" }]} />
            </div>
          } styles={{ body: { padding: "8px 4px" } }}>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <AreaChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="#dbeafe" name="Total" />
                  <Area type="monotone" dataKey="paid" stroke="#10b981" fill="#d1fae5" name="Paid" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Campaign Leads</span>} styles={{ body: { padding: "8px 4px" } }}>
            {campaignPerformance.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={campaignPerformance} layout="vertical" margin={{ left: 55, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 9 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={50} />
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="paid" stackId="a" fill="#10b981" name="Paid" />
                  <Bar dataKey="notPaid" stackId="a" fill="#fbbf24" name="Not Paid" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>}
          </Card>
        </Col>
      </Row>

      {/* ── Row 2: Campaign Revenue + Conv Rate Table + Device + Browser ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Campaign Revenue</span>} styles={{ body: { padding: "8px 4px" } }}>
            {campaignPerformance.filter((c) => c.revenue > 0).length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={campaignPerformance.filter((c) => c.revenue > 0)} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => `$${v}`} />
                  <RTooltip contentStyle={{ fontSize: 11 }} formatter={(v: any) => `$${Number(v || 0).toFixed(2)}`} />
                  <Bar dataKey="revenue" fill="#8b5cf6" name="Revenue" radius={[3, 3, 0, 0]}>
                    {campaignPerformance.filter((c) => c.revenue > 0).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No revenue data</div>}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Campaign Conversion Rates</span>} styles={{ body: { padding: "4px 12px", maxHeight: 210, overflowY: "auto" } }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb", color: "#6b7280" }}>
                  <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 600 }}>Campaign</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Leads</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Paid</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Conv %</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {campaignPerformance.map((c) => (
                  <tr key={c.name} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "4px 0", maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <Tooltip title={c.name}>{c.name}</Tooltip>
                    </td>
                    <td style={{ textAlign: "right", padding: "4px 0" }}>{c.total}</td>
                    <td style={{ textAlign: "right", padding: "4px 0", color: "#059669" }}>{c.paid}</td>
                    <td style={{ textAlign: "right", padding: "4px 0" }}>
                      <span style={{ color: Number(c.convRate) >= 50 ? "#059669" : Number(c.convRate) >= 20 ? "#f59e0b" : "#ef4444", fontWeight: 600 }}>{c.convRate}%</span>
                    </td>
                    <td style={{ textAlign: "right", padding: "4px 0", color: "#7c3aed" }}>${c.revenue.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Col>

        <Col xs={12} md={4}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Device</span>} styles={{ body: { padding: "0 4px" } }}>
            {deviceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={deviceData} cx="50%" cy="50%" outerRadius={55} dataKey="value" label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 9 }}>
                    {deviceData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>}
          </Card>
        </Col>

        <Col xs={12} md={4}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Browser</span>} styles={{ body: { padding: "0 4px" } }}>
            {browserData.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <PieChart>
                  <Pie data={browserData} cx="50%" cy="50%" outerRadius={55} dataKey="value" label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 9 }}>
                    {browserData.map((_, i) => <Cell key={i} fill={PIE_COLORS[(i + 3) % PIE_COLORS.length]} />)}
                  </Pie>
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>}
          </Card>
        </Col>
      </Row>

      {/* ── Row 3: Top Locations + Top Categories + Top Plans ── */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Top Locations</span>} styles={{ body: { padding: "8px 4px" } }}>
            {locationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={locationData} margin={{ left: 8, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="paid" stackId="a" fill="#10b981" name="Paid" />
                  <Bar dataKey="total" fill="#3b82f6" name="Total" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Top Categories</span>} styles={{ body: { padding: "8px 4px" } }}>
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height={170}>
                <BarChart data={categoryData} layout="vertical" margin={{ left: 55, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 9 }} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={50} />
                  <RTooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" fill="#3b82f6" name="Total" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="paid" fill="#10b981" name="Paid" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card size="small" title={<span style={{ fontSize: 13, fontWeight: 600 }}>Plans & Revenue</span>} styles={{ body: { padding: "4px 12px", maxHeight: 200, overflowY: "auto" } }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #e5e7eb", color: "#6b7280" }}>
                  <th style={{ textAlign: "left", padding: "4px 0", fontWeight: 600 }}>Plan</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Price</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Sold</th>
                  <th style={{ textAlign: "right", padding: "4px 0", fontWeight: 600 }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {planData.map((p) => (
                  <tr key={p.name} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "4px 0", fontWeight: 500 }}>{p.name}</td>
                    <td style={{ textAlign: "right", padding: "4px 0" }}>${p.price}</td>
                    <td style={{ textAlign: "right", padding: "4px 0" }}>{p.total}</td>
                    <td style={{ textAlign: "right", padding: "4px 0", color: "#7c3aed", fontWeight: 600 }}>${p.revenue.toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </Col>
      </Row>

      <FilterTabWrapper>
        <StatusFilters>
          {topFilterOptions.map((filter) => {
            const isActive = topFilter === filter.key;
            const chipTextColor = filter.colors.text;
            const chipBackground = filter.colors.background;
            return (
              <FilterButton
                key={filter.key}
                active={isActive}
                onClick={() => handleTopFilterChange(filter.key)}
                $backgroundColor={filter.colors.background}
                $borderColor={filter.colors.border}
                $textColor={filter.colors.text}
              >
                {isActive && <CheckCircleFilled style={{ marginRight: 4, color: chipBackground }} />}
                <span style={{ fontSize: "14px", fontWeight: 600 }}>{filter.label}</span>
                <CountBadge $textColor={isActive ? "#ffffff" : chipTextColor} className="status-count-badge">
                  {getTopCount(filter.key)}
                </CountBadge>
              </FilterButton>
            );
          })}

          <Select
            value={subStatus}
            onChange={handleSubStatusChange}
            options={filteredSubStatusOptions}
            style={{ minWidth: 170 }}
            size="middle"
          />
          <Select
            value={campaignFilter}
            onChange={(val) => { setCampaignFilter(val); setCurrentPage(1); }}
            style={{ minWidth: 160 }}
            size="middle"
            options={[
              { value: "all", label: "All Campaigns" },
              { value: "_direct", label: "Direct (no campaign)" },
              { value: "_unmatched", label: "Unmatched" },
              ...campaigns.map((c) => ({ value: c.id, label: c.label })),
            ]}
          />
        </StatusFilters>

        <FilterInputs>
          <StyledSelect
            allowClear
            value={searchField}
            options={[
              { value: "lost.trackingId", label: "Claim #" },
              { value: "user.name", label: "Customer Name" },
              { value: "user.email", label: "Email" },
              { value: "category.name", label: "Category" },
              { value: "establishmentLocation.name", label: "Location" },
              { value: "lost.visitorInfo", label: "Entry URL" },
            ]}
            onChange={(value) => handleSearchFieldChange(value as string | null)}
            placeholder="Filter by"
            size="middle"
          />
          <Input
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Tooltip title={isDateRangeSelected ? formattedDateRange : "Select date range"}>
            <Button
              icon={<CalendarOutlined />}
              onClick={() => setIsDateModalOpen(true)}
              type={isDateRangeSelected ? "primary" : "default"}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, height: 32, minWidth: 32, padding: "0 8px" }}
            >
              {isDateRangeSelected && (
                <CloseCircleOutlined style={{ marginLeft: 4 }} onClick={(e) => { e.stopPropagation(); handleClearDateFilter(); }} />
              )}
            </Button>
          </Tooltip>
        </FilterInputs>
      </FilterTabWrapper>

      <LogsTable
        columns={columns}
        dataSource={filteredData.map((item) => ({ ...item, key: item.id }))}
        loading={loadingStatus === RequestResults.pending}
        scroll={{ x: 1310 }}
        pagination={{
          current: currentPage,
          total: campaignFilter !== "all" ? filteredData.length : counts,
          pageSize: currentLimit,
          showSizeChanger: true,
          onChange: handlePageChange,
          onShowSizeChange: (_, size) => handleLimitChange(size),
        }}
      />

      <DatePickerModal
        isModalOpen={isDateModalOpen}
        handleOk={handleDateModalOk}
        handleCancel={() => setIsDateModalOpen(false)}
        values={{ dateField, fromDate, toDate }}
      />

      <Modal
        title="Manage Campaigns"
        open={isCampaignModalOpen}
        onCancel={() => { setIsCampaignModalOpen(false); handleCancelEdit(); }}
        footer={null}
        width={640}
      >
        <Form form={campaignForm} layout="inline" style={{ marginBottom: 16, gap: 8, display: "flex", flexWrap: "wrap" }}>
          <Form.Item name="label" rules={[{ required: true, message: "Label required" }]} style={{ flex: 1, minWidth: 140 }}>
            <Input placeholder="Campaign Label (e.g. Google Ads)" />
          </Form.Item>
          <Form.Item name="urlPattern" rules={[{ required: true, message: "URL pattern required" }]} style={{ flex: 1, minWidth: 180 }}>
            <Input placeholder="URL pattern (e.g. utm_source=google)" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" icon={editingCampaign ? <EditOutlined /> : <PlusOutlined />} onClick={handleCampaignSave}>
                {editingCampaign ? "Update" : "Add"}
              </Button>
              {editingCampaign && <Button onClick={handleCancelEdit}>Cancel</Button>}
            </Space>
          </Form.Item>
        </Form>

        <div style={{ maxHeight: 350, overflowY: "auto" }}>
          {campaigns.map((c) => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f0f0" }}>
              <div>
                <div style={{ fontWeight: 500 }}>{c.label}</div>
                <div style={{ fontSize: 12, color: "#888" }}>{c.urlPattern}</div>
              </div>
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => handleEditCampaign(c)} />
                <Popconfirm title="Delete this campaign?" onConfirm={() => handleCampaignDelete(c.id)} okText="Yes" cancelText="No">
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            </div>
          ))}
          {campaigns.length === 0 && <div style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>No campaigns yet. Add one above.</div>}
        </div>
      </Modal>

      <TestClaimsModal
        open={isTestClaimsModalOpen}
        onClose={() => setIsTestClaimsModalOpen(false)}
        onChange={refreshTrackingData}
      />
    </Container>
  );
};

export default ConversionTracking;
